import { Component, ElementRef, ErrorHandler, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { CourseRepositoryService } from '../../services/course-repository.service';
import { AIPromptRequestStatusAllItems, CourseEntity, CourseLessonEntity, CourseModuleEntity, LessonTypeAllItems } from '../../../../meta-data/app-meta-data.service';
import { DwComponent, DwComponentType, DwRoutingService, DwSecurityUserService } from '@devwareapps/devware-cap';
import { Router } from '@angular/router';
import { CourseNavigationService } from '../../services/course-navigation.service';
import { LessonReferenceInfo } from '../../models/lesson-reference-info.model';
import { CourseProgressItem, LessonProgressItem, ModuleProgressItem } from '../../models/course-progress-hierarchy.model';



@DwComponent({
  name: 'Course Navigation',
  key: 'courseNavigation',
  isGlobal: true,
  componentType: DwComponentType.sideMenuReplacement
}) 
@Component({ 
  selector: 'app-course-navigation', 
  templateUrl: './course-navigation.component.html',
  styleUrls: ['./course-navigation.component.scss']
})
export class CourseNavigationComponent implements OnInit {
  @ViewChild('navHeader', { read: ElementRef })
  navHeaderRef: ElementRef<HTMLElement>;

  @ViewChildren('moduleRow', { read: ElementRef })
  moduleRowRefs: QueryList<ElementRef<HTMLElement>>;

  @ViewChildren('lessonRow', { read: ElementRef })
  lessonRowRefs: QueryList<ElementRef<HTMLElement>>;

  currentLessonReference: LessonReferenceInfo;
  courseProgress: CourseProgressItem;
  loadingCourse = false;

  // Static title widths (%) for the loading skeleton's module rows.
  readonly skeletonModuleWidths = [72, 58, 84, 64, 76, 52];

  moduleCollapsed: { [moduleId: number]: boolean } = {};

  moduleDetails: { [moduleId: number]: { collapsed: boolean, hasAiSummary: boolean } } = {};

  quizLessonType = LessonTypeAllItems.Quiz;
  videoLessonType = LessonTypeAllItems.Video;
  documentLessonType = LessonTypeAllItems.ProTools;

  // Ring geometry for in-progress status icon — 2πr at r=9.
  readonly ringCircumference = 2 * Math.PI * 9;

  constructor(
    private courseRepositoryService: CourseRepositoryService,
    private courseNavigationService : CourseNavigationService,
    private dwSecurityUserService: DwSecurityUserService,
    private router: Router,
    private dwRoutingService: DwRoutingService,
    private readonly errorHandler: ErrorHandler) {
    }
 
  ngOnInit(): void {
    this.setCurrentLesson(); 
    this.loadCourse();

    this.dwRoutingService.getRoutesChanges().subscribe(() => {
      this.setCurrentLesson();
    });
 
    this.courseRepositoryService.getCourseProgressUpdate()
      .subscribe(() => {
        setTimeout(() => {
          this.loadCourse();
        },50);
      });

    this.dwSecurityUserService.securityContextChanged().subscribe(() => {
      this.loadCourse();
    });
  }

  toggleModuleCollapse(moduleProgress: ModuleProgressItem) {
    const moduleId = moduleProgress?.module?.CourseModuleId;
    if (moduleId == null) {
      return;
    }

    const collapsed = !this.moduleCollapsed[moduleId];
    this.setModuleCollapsedState(moduleId, collapsed);
  }

  loadCourse() {
    const courseId = this.currentLessonReference?.courseId;
    if (!courseId) {
      this.loadingCourse = false;
      this.courseProgress = null;
      this.moduleCollapsed = {};
      this.moduleDetails = {};
      return;
    }

    this.loadingCourse = true;
    this.courseRepositoryService.getCourseProgress(courseId)
      .subscribe({
        next: courseProgress => {
          this.loadingCourse = false;
          this.courseProgress = courseProgress;

          this.setupModuleCollapsed();
        },
        error: error => {
          this.loadingCourse = false;
          this.errorHandler.handleError(error);
        }
      });
  }

  setCurrentLesson() {
    const previousModuleId = this.currentLessonReference?.moduleId;
    const previousLessonId = this.currentLessonReference?.lessonId;
    const newLessonReference = this.courseNavigationService.getCourseUrlParameters(this.router.url);

    if(this.currentLessonReference && this.currentLessonReference?.courseId != newLessonReference?.courseId) {
      this.loadCourse();
    }

    this.currentLessonReference = newLessonReference;

    this.setupModuleCollapsed();

    if (previousModuleId !== this.currentLessonReference?.moduleId) {
      this.syncExpandedModulesToRoute();
      return;
    }

    if (previousLessonId !== this.currentLessonReference?.lessonId) {
      this.scrollCurrentSelectionIntoView();
    }
  }

  navigateToCourse() {
    this.courseNavigationService.navigateToCourse(this.currentLessonReference?.courseId);
  }

  getLessonIconState(lessonNav: LessonProgressItem, module: CourseModuleEntity): 'complete' | 'current' | 'in-progress' | 'quiz' | 'not-started' {
    if (lessonNav.isComplete) {
      return 'complete';
    }

    const isCurrent = this.currentLessonReference?.lessonId == lessonNav.lesson.CourseLessonId;
    if (isCurrent) {
      return 'current';
    }

    if (lessonNav.percentComplete > 0) {
      return 'in-progress';
    }

    if (lessonNav.lesson.LessonTypeId == this.quizLessonType) {
      return 'quiz';
    }

    return 'not-started';
  }

  getRingOffset(percentComplete: number): number {
    const pct = Math.max(0, Math.min(100, percentComplete || 0));
    return this.ringCircumference * (1 - pct / 100);
  }

  navigateToModule(module: CourseModuleEntity) {
    this.courseNavigationService.navigateToModule(module.CourseId, module.CourseModuleId);
    this.hideMobile();
  }

  navigateToLesson(module: CourseModuleEntity, lesson: CourseLessonEntity) {

    if (lesson.IsPreview) {
      console.warn('Attempting to navigate to a preview lesson. Navigation prevented.', lesson);
      return;
    }
    
    this.courseNavigationService.navigateToLesson( { courseId: module.CourseId, moduleId: module.CourseModuleId, lessonId: lesson.CourseLessonId});
    this.hideMobile();
  }

  hideMobile() {
		if (document.body.classList.contains('sidebar-show')) {
            document.body.classList.toggle('sidebar-show');
        }
	}

  private setupModuleCollapsed() {
    if (!this.courseProgress?.modules?.length) {
      this.moduleCollapsed = {};
      this.moduleDetails = {};
      return;
    }

    // Preserve prior expansions across courseProgress updates so a user's
    // manual open state isn't clobbered when progress reloads.
    const prior = this.moduleCollapsed;
    const initialized = Object.keys(prior).length > 0;
    this.moduleCollapsed = {};
    this.moduleDetails = {};

    for (const moduleProgress of this.courseProgress.modules) {
      const id = moduleProgress.module.CourseModuleId;
      const collapsed = initialized ? (prior[id] ?? true) : true;

      this.moduleDetails[id] = {
        collapsed,
        hasAiSummary: (moduleProgress.moduleProgress?.AIPromptRequest?.AIPromptRequestStatusId == AIPromptRequestStatusAllItems.Success)
      };

      this.moduleCollapsed[id] = collapsed;
    }

    // Only auto-expand on first setup — subsequent updates respect the user.
    if (initialized) {
      return;
    }

    const autoExpandId = this.getAutoExpandModuleId();

    if (autoExpandId == null || !this.moduleDetails[autoExpandId]) {
      return;
    }

    // Defer the unfold so it sequences AFTER the sidebar's slide-in instead
    // of animating concurrently. Sidebar transition is var(--duration-slow) =
    // 300ms (see cap-variables.scss); the extra 50ms lets the slide visually
    // settle before the lesson list opens. Skip the delay under reduced-motion
    // since the slide collapses to ~1ms there.
    const reduceMotion = typeof window !== 'undefined'
      && window.matchMedia
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const delay = reduceMotion ? 0 : 350;
    const targetId = autoExpandId;

    setTimeout(() => {
      // Bail if the user manually expanded/toggled before the timer fired.
      if (this.moduleCollapsed[targetId] !== true || !this.moduleDetails[targetId]) {
        return;
      }
      this.setModuleCollapsedState(targetId, false);
      this.scrollCurrentSelectionIntoView(targetId);
    }, delay);
  }

  private getAutoExpandModuleId(): number | null {
    const candidateIds = [
      this.currentLessonReference?.moduleId,
      this.courseProgress?.furtherestLesson?.CourseModuleId,
      this.courseProgress?.modules?.[0]?.module?.CourseModuleId
    ];

    for (const candidateId of candidateIds) {
      if (candidateId != null && this.moduleDetails[candidateId]) {
        return candidateId;
      }
    }

    return null;
  }

  private setModuleCollapsedState(moduleId: number, collapsed: boolean) {
    if (moduleId == null || !this.moduleDetails[moduleId]) {
      return;
    }

    this.moduleCollapsed[moduleId] = collapsed;
    this.moduleDetails[moduleId].collapsed = collapsed;
  }

  private syncExpandedModulesToRoute() {
    const currentModuleId = this.currentLessonReference?.moduleId;

    if (currentModuleId == null) {
      this.collapseAllModules();
      return;
    }

    if (this.moduleCollapsed[currentModuleId] == null) {
      return;
    }

    this.collapseAllModules();
    this.setModuleCollapsedState(currentModuleId, false);
    this.scrollCurrentSelectionIntoView(currentModuleId);
  }

  private collapseAllModules() {
    Object.keys(this.moduleCollapsed).forEach(moduleId => {
      this.setModuleCollapsedState(Number(moduleId), true);
    });
  }

  private scrollCurrentSelectionIntoView(moduleId?: number | null) {
    setTimeout(() => {
      const targetRow = this.getCurrentSelectionRow(moduleId);
      const container = this.findScrollContainer(targetRow);
      const headerHeight = this.navHeaderRef?.nativeElement?.offsetHeight || 0;

      if (!container || !targetRow) {
        return;
      }

      const reduceMotion = typeof window !== 'undefined'
        && window.matchMedia
        && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const scrollTop = targetRow.getBoundingClientRect().top
        - container.getBoundingClientRect().top
        + container.scrollTop
        - headerHeight;

      const nextTop = Math.max(0, scrollTop);

      if (typeof container.scrollTo === 'function') {
        container.scrollTo({
          top: nextTop,
          behavior: reduceMotion ? 'auto' : 'smooth'
        });
        return;
      }

      container.scrollTop = nextTop;
    });
  }

  private getCurrentSelectionRow(moduleId?: number | null): HTMLElement | null {
    const lessonId = this.currentLessonReference?.lessonId;
    const lessonRow = lessonId != null ? this.getLessonRow(lessonId) : null;

    if (lessonRow) {
      return lessonRow;
    }

    const fallbackModuleId = moduleId ?? this.currentLessonReference?.moduleId;
    return fallbackModuleId != null ? this.getModuleRow(fallbackModuleId) : null;
  }

  private getModuleRow(moduleId: number): HTMLElement | null {
    return this.moduleRowRefs?.find(ref =>
      Number(ref.nativeElement.getAttribute('data-module-id')) === moduleId
    )?.nativeElement || null;
  }

  private getLessonRow(lessonId: number): HTMLElement | null {
    return this.lessonRowRefs?.find(ref =>
      Number(ref.nativeElement.getAttribute('data-lesson-id')) === lessonId
    )?.nativeElement || null;
  }

  private findScrollContainer(moduleRow: HTMLElement | null): HTMLElement | null {
    if (!moduleRow) {
      return null;
    }

    const replacementNav = moduleRow.closest('.replacement-nav') as HTMLElement | null;
    if (replacementNav) {
      return replacementNav;
    }

    const sidebar = moduleRow.closest('.sidebar') as HTMLElement | null;
    if (sidebar) {
      return sidebar;
    }

    let ancestor = moduleRow.parentElement;
    while (ancestor) {
      const style = window.getComputedStyle(ancestor);
      const canScroll = /(auto|scroll)/.test(style.overflowY || '');

      if (canScroll && ancestor.scrollHeight > ancestor.clientHeight) {
        return ancestor;
      }

      ancestor = ancestor.parentElement;
    }

    return null;
  }

}  

export interface ModuleDetail {
  collapsed: boolean;
  hasAiSummary: boolean;
}
